import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { authApi } from '../api/auth';
import type { AccessTag, AuthTokens, MeResponse, Permission } from '../api/types';
import {
  clearSession,
  readSession,
  sessionFromTokens,
  subscribeSession,
  writeSession,
  type SessionEndReason,
} from './session';

/** The one-line notice the sign-in page shows after a session ended on its own. */
export const SESSION_ENDED_NOTICE = 'Your session has ended. Sign in again to continue.';

export type AuthState =
  | { status: 'loading' }
  | { status: 'anonymous'; reason?: SessionEndReason }
  | { status: 'authenticated'; me: MeResponse };

export interface AuthContextValue {
  state: AuthState;
  /** Stores the tokens, loads /auth/me and switches to `authenticated`. */
  completeSignIn(tokens: AuthTokens): Promise<MeResponse>;
  /** Drops the session and the query cache locally and revokes the refresh token (best effort). */
  signOut(): Promise<void>;
  /** Re-reads /auth/me (after a profile change). */
  refreshMe(): Promise<MeResponse | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Restores the session from localStorage on load by calling /auth/me (which
 * also gets the access token renewed, server-side, when it had expired), and
 * drops to `anonymous` whenever the client clears the session (a 401, signed
 * out in another tab). Whenever the session ends, for any reason, the query
 * cache is emptied so nothing fetched for one account survives into the next
 * sign-in.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() =>
    readSession() ? { status: 'loading' } : { status: 'anonymous' },
  );
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!readSession()) return;
    let cancelled = false;
    const restore = async () => {
      try {
        const me = await authApi.me();
        if (!cancelled) setState({ status: 'authenticated', me });
      } catch {
        if (cancelled) return;
        // The client may already have cleared the session with a reason (session ended);
        // that notification set the state, so only tidy up when it did not.
        if (readSession()) clearSession();
        setState((prev) => (prev.status === 'anonymous' ? prev : { status: 'anonymous' }));
      }
    };
    void restore();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(
    () =>
      subscribeSession((session, reason) => {
        if (session) return;
        queryClient.clear();
        setState((prev) =>
          prev.status === 'anonymous' && !reason ? prev : { status: 'anonymous', reason },
        );
      }),
    [queryClient],
  );

  const completeSignIn = useCallback(async (tokens: AuthTokens) => {
    writeSession(sessionFromTokens(tokens, readSession()));
    try {
      const me = await authApi.me();
      setState({ status: 'authenticated', me });
      return me;
    } catch (error) {
      clearSession();
      throw error;
    }
  }, []);

  const signOut = useCallback(async () => {
    const session = readSession();
    clearSession();
    queryClient.clear();
    setState({ status: 'anonymous' });
    if (session?.refreshToken) {
      await authApi.logout(session.refreshToken).catch(() => undefined);
    }
  }, [queryClient]);

  const refreshMe = useCallback(async () => {
    if (!readSession()) return null;
    const me = await authApi.me();
    setState({ status: 'authenticated', me });
    return me;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ state, completeSignIn, signOut, refreshMe }),
    [state, completeSignIn, signOut, refreshMe],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

/** The signed-in user; only valid under a <RequireAuth> route. */
export function useMe(): MeResponse {
  const { state } = useAuth();
  if (state.status !== 'authenticated') throw new Error('useMe called while not signed in');
  return state.me;
}

/** True when the API granted this permission to the signed-in user's role. */
export function can(me: MeResponse, permission: Permission): boolean {
  return me.principal.permissions.includes(permission);
}

/** True when the API tagged the signed-in account with this access tag (admin / staff / tenant). */
export function hasAccess(me: MeResponse, tag: AccessTag): boolean {
  return me.principal.access.includes(tag);
}

/** Where a signed-in user finds their own tenant page; null for staff and for accounts without a tenant. */
export function ownTenantPath(me: MeResponse): string | null {
  return me.principal.tenantId ? `/tenant/${me.principal.tenantId}` : null;
}

/** Where a signed-in user lands: staff console or tenant portal. */
export function homeFor(me: MeResponse): string {
  if (me.user.isDeactivated) return '/deactivated';
  return me.principal.actorKind === 'staff' ? '/staff' : '/portal';
}
